// CHAT-UX-01 delta (doc 29 §17.3 D-CX-7 / D-CX-8, §17.8) -- undelimited math in a
// STUDENT's own text, for display only.
//
// A student types `x^2 + 5y = z چیه`, not `$x^2 + 5y = z$`: the Markdown + math
// renderer only typesets delimited math, so the Human local UAT saw the
// formula as typed. This module finds the mathematical runs inside one piece
// of plain text and builds the TeX to DISPLAY them with. It never changes the
// text itself: what is stored, sent and edited is the student's own string.
//
// It is deliberately conservative (the false-positive guard of doc 29 §17.3):
// a run is math only with a strong signal, and anything that looks like a
// unit, a date, a phone number, a URL, a version, an identifier or prose stays
// as typed. A false negative costs a formula shown as text; a false positive
// re-writes a student's sentence. When in doubt the text stays as it is.
//
// Pure: no DOM, no markdown-it, no KaTeX. The renderer (markdownRenderer.js)
// asks KaTeX whether it accepts each candidate; a run KaTeX refuses stays as
// typed too.

// ---- the alphabet of a run
const LATIN = 'A-Za-z'
const LATIN_DIGITS = '0-9'
const PERSIAN_DIGITS = '۰-۹٠-٩'
const DIGITS = LATIN_DIGITS + PERSIAN_DIGITS
const OPERAND = LATIN + DIGITS
// What a run may be made of besides letters, digits and spaces.
const SIGNS = "+\\-*/^_=<>()[\\]{}.,|!%'\\\\×÷±≤≥≠√π∞−²³¹"
const RUN_CHAR = new RegExp(`[${LATIN}${DIGITS} ${SIGNS}]`)
const HAS_OPERAND = new RegExp(`[${OPERAND}]`)

// The words a run may hold. Every other Latin word of three or more letters
// means prose, a unit, an identifier or a URL -- not math.
const FUNCTIONS = [
  'sin',
  'cos',
  'tan',
  'cot',
  'sec',
  'csc',
  'arcsin',
  'arccos',
  'arctan',
  'sinh',
  'cosh',
  'tanh',
  'log',
  'ln',
  'lg',
  'sqrt',
  'lim',
  'max',
  'min',
  'exp',
]
const GREEK = [
  'pi',
  'theta',
  'delta',
  'Delta',
  'lambda',
  'sigma',
  'Sigma',
  'omega',
  'Omega',
  'phi',
  'gamma',
]
const KNOWN_WORDS = new Set([...FUNCTIONS, ...GREEK])

// A number followed by one of these is a measurement, never a product.
const UNITS = new Set(
  'km m cm mm nm kg g mg s ms h hr min l ml N J W V A K Hz kHz MHz Pa kPa mol cal kcal kW kWh mph rpm deg rad'.split(
    ' '
  )
)

const MAX_RUN = 200

const OPERAND_END = `[${OPERAND})\\]}!|]`
const OPERAND_START = `[${OPERAND}(\\[{|\\-\\\\√π∞±]`
// Strong signals: the run is math.
const POWER_OR_INDEX = new RegExp(`${OPERAND_END}[\\^_]${OPERAND_START}`)
const RELATION = new RegExp(
  `${OPERAND_END} *(?:<=|>=|!=|[=<>≤≥≠]) *${OPERAND_START}`
)
const COMMAND = /\\[A-Za-z]+/
const FUNCTION_CALL = new RegExp(
  `(?:^|[^${LATIN}])(?:${FUNCTIONS.join('|')})\\(|√`
)
const ADDITIVE = new RegExp(`${OPERAND_END} *[+*×÷] *${OPERAND_START}`)
const DIVISIVE = new RegExp(`${OPERAND_END} *[\\-/−] *${OPERAND_START}`)
const HAS_LETTER = new RegExp(`[${LATIN}]`)
const HAS_DIGIT = new RegExp(`[${DIGITS}]`)

// Reasons a run is NOT math, whatever else it holds.
const NUMBER_THEN_WORD = new RegExp(`[${DIGITS}] +[${LATIN}]{2,}`)
const NUMBER_THEN_UNIT = new RegExp(
  `[${DIGITS}] ?([${LATIN}]+)(?![${LATIN}])`,
  'g'
)
const LATIN_WORD = new RegExp(`(^|[^${LATIN}\\\\])([${LATIN}]{3,})`, 'g')
const STARTS_WITH_OPERATOR = /^[+*/=<>^_×÷≤≥≠.,]/
const ENDS_WITH_OPERATOR = /[+\-*/=<>^_\\×÷−≤≥≠,]$/

function isRunChar(char) {
  return RUN_CHAR.test(char)
}

// The character before / after a run decides whether it stands on its own.
// Persian text (anything beyond ASCII that is not a run character) and the
// ends of the text do; so does a colon before («حل من: (x-2)(x+3)=0») and a
// question mark, colon or semicolon after («x=5?»). Any other ASCII neighbour
// (`@`, `#`, `"`, `/` of a URL ...) means the run is part of something else.
function standsAlone(before, after) {
  const okBefore =
    before === '' || before.charCodeAt(0) >= 128 || before === ':'
  const okAfter =
    after === '' || after.charCodeAt(0) >= 128 || '?:;'.includes(after)
  return okBefore && okAfter
}

function balanced(run) {
  const stack = []
  const pairs = { ')': '(', ']': '[', '}': '{' }
  for (const char of run) {
    if ('([{'.includes(char)) stack.push(char)
    else if (char in pairs) {
      if (stack.pop() !== pairs[char]) return false
    }
  }
  return stack.length === 0
}

// Trims what is sentence punctuation, not mathematics, from the ends.
function trimRun(run) {
  let out = run.replace(/^[ .,]+/, '').replace(/[ .,]+$/, '')
  // A trailing `!` is a factorial after a number or a bracket, else prose.
  if (/[^0-9۰-۹٠-٩)\]]!$/.test(out)) out = out.slice(0, -1)
  return out.replace(/[ .,]+$/, '')
}

function hasUnknownWord(run) {
  LATIN_WORD.lastIndex = 0
  let match
  while ((match = LATIN_WORD.exec(run))) {
    if (!KNOWN_WORDS.has(match[2])) return true
  }
  return false
}

function hasUnit(run) {
  NUMBER_THEN_UNIT.lastIndex = 0
  let match
  while ((match = NUMBER_THEN_UNIT.exec(run))) {
    if (UNITS.has(match[1])) return true
  }
  return false
}

// `x²` is `x^2` typed with the keyboard's own superscript.
const SUPERSCRIPTS = { '²': '^2', '³': '^3', '¹': '^1' }
const writeSuperscripts = text =>
  text.replace(/[²³¹]/g, char => SUPERSCRIPTS[char])

/**
 * Whether one trimmed run is mathematics, by the rules of doc 29 §17.8.
 * @param {string} typed
 * @returns {boolean}
 */
export function isMathRun(typed) {
  const run = writeSuperscripts(typed)
  if (run.length < 2 || run.length > MAX_RUN) return false
  if (!HAS_OPERAND.test(run)) return false
  if (STARTS_WITH_OPERATOR.test(run) || ENDS_WITH_OPERATOR.test(run))
    return false
  if (!balanced(run)) return false
  if (run.includes('**') || run.includes('//')) return false
  if (hasUnknownWord(run)) return false
  if (NUMBER_THEN_WORD.test(run) || hasUnit(run)) return false
  const hasCommand = COMMAND.test(run)
  if (/[{}]/.test(run) && !hasCommand && !/[\^_]\{/.test(run)) return false

  if (POWER_OR_INDEX.test(run)) return true
  if (RELATION.test(run)) return true
  if (hasCommand) return true
  if (FUNCTION_CALL.test(run)) return true
  const hasLetter = HAS_LETTER.test(run)
  if (hasLetter && ADDITIVE.test(run)) return true
  if (hasLetter && HAS_DIGIT.test(run) && DIVISIVE.test(run)) return true
  return false
}

const PERSIAN_TO_LATIN = char => {
  const code = char.charCodeAt(0)
  if (code >= 0x06f0 && code <= 0x06f9)
    return String.fromCharCode(code - 0x06f0 + 48)
  if (code >= 0x0660 && code <= 0x0669)
    return String.fromCharCode(code - 0x0660 + 48)
  return char
}

/**
 * The TeX that DISPLAYS a run the student wrote without delimiters. Only what
 * TeX needs is changed: Persian digits become the Latin digits every formula
 * in this chat is set in, `^12` gets its braces, `*` is the multiplication
 * sign it stands for (D-CX-8), `sqrt(…)` and `sin` become their commands.
 * Nothing here is ever written back to the student's text.
 * @param {string} run
 * @returns {string}
 */
export function toDisplayTex(run) {
  let tex = writeSuperscripts(run).replace(/[۰-۹٠-٩]/g, PERSIAN_TO_LATIN)
  tex = tex
    .replace(/−/g, '-')
    .replace(/<=/g, '\\le ')
    .replace(/>=/g, '\\ge ')
    .replace(/!=/g, '\\ne ')
    .replace(/≤/g, '\\le ')
    .replace(/≥/g, '\\ge ')
    .replace(/≠/g, '\\ne ')
    .replace(/[*×]/g, '\\times ')
    .replace(/÷/g, '\\div ')
    .replace(/±/g, '\\pm ')
    .replace(/∞/g, '\\infty ')
    .replace(/π/g, '\\pi ')
    .replace(/%/g, '\\%')
  // `sqrt(x+1)` / `√(x+1)` -> `\sqrt{x+1}` (one level of brackets).
  tex = tex.replace(/(?:sqrt|√)\(([^()]*)\)/g, '\\sqrt{$1}')
  tex = tex.replace(/√/g, '\\sqrt ')
  // `x^12`, `x^-1`, `a_max` -> braced; a single character needs none.
  tex = tex.replace(/([\^_])(-[A-Za-z0-9]+|[A-Za-z0-9]{2,})/g, '$1{$2}')
  // `sin x`, `log(…)`, `pi`, `theta` -> their commands (never twice).
  tex = tex.replace(
    new RegExp(
      `(^|[^A-Za-z\\\\])(${[...KNOWN_WORDS].join('|')})(?![A-Za-z])`,
      'g'
    ),
    '$1\\$2 '
  )
  return tex.replace(/ +/g, ' ').trim()
}

/**
 * Finds the mathematical runs in one piece of plain text (one markdown-it
 * `text` token: no newline, no Markdown syntax left in it).
 * @param {string} text
 * @returns {{start: number, end: number, source: string, tex: string}[]}
 *   in text order, non-overlapping; `source` is the run exactly as typed
 */
export function findImplicitMath(text) {
  const found = []
  if (typeof text !== 'string' || !text) return found
  let i = 0
  while (i < text.length) {
    if (!isRunChar(text[i])) {
      i += 1
      continue
    }
    let j = i
    while (j < text.length && isRunChar(text[j])) j += 1
    const raw = text.slice(i, j)
    const before = i > 0 ? text[i - 1] : ''
    const after = j < text.length ? text[j] : ''
    if (standsAlone(before, after)) {
      const run = trimRun(raw)
      if (run && isMathRun(run)) {
        const start = i + raw.indexOf(run)
        found.push({
          start,
          end: start + run.length,
          source: run,
          tex: toDisplayTex(run),
        })
      }
    }
    i = j
  }
  return found
}

export default findImplicitMath
